// Settings — account, yard, team, preferences, sensor, danger zone.
// Long scrolling page. Sticky anchor rail on desktop only.
// Two modal variants: invite teammate, remove member.

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AVATAR_TONES } from "../lib/avatarTones";
import { useIsMobile } from "../lib/useMediaQuery";
import { useCurrentUser } from "../lib/useCurrentUser";
import { useUserYard } from "../lib/yard";
import {
  MOCK_HORSES,
  MOCK_YARD,
  MOCK_TEAM,
  MOCK_ACCOUNT,
  MOCK_PREFS,
} from "../lib/mockData";
import type { MockMember, MockPrefs } from "../lib/mockData";
import "../styles/dashboard.css";
import "../styles/settings.css";

const SIc = {
  back: <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 3L5 8L10 13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  account: <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="5" r="2.5" stroke="currentColor" strokeWidth="1.3" /><path d="M2.5 12c0-2.5 2-4 4.5-4s4.5 1.5 4.5 4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" /></svg>,
  yard: <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 12V6L7 2.5L12 6V12" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" /><path d="M5.5 12V8.5H8.5V12" stroke="currentColor" strokeWidth="1.3" /></svg>,
  team: <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="5" cy="5.5" r="2" stroke="currentColor" strokeWidth="1.3" /><circle cx="10" cy="5" r="1.5" stroke="currentColor" strokeWidth="1.3" /><path d="M1.5 11.5c0-2 1.5-3.5 3.5-3.5s3.5 1.5 3.5 3.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" /><path d="M9 11.5c0-1.5 1-2.5 2.5-2.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" /></svg>,
  prefs: <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 4h6M11 4h1M2 10h1M6 10h6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" /><circle cx="9.5" cy="4" r="1.5" stroke="currentColor" strokeWidth="1.3" /><circle cx="4.5" cy="10" r="1.5" stroke="currentColor" strokeWidth="1.3" /></svg>,
  sensor: <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="1.5" fill="currentColor" /><path d="M4 4Q2 7 4 10M10 4Q12 7 10 10" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" /></svg>,
  danger: <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 1.5L13 12H1L7 1.5z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" /><path d="M7 5.5V8.5M7 10V10.1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" /></svg>,
  chev: <svg width="12" height="12" viewBox="0 0 14 14" fill="none"><path d="M4 5L7 8L10 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  arrow: <svg width="12" height="12" viewBox="0 0 14 14" fill="none"><path d="M3 7H11M8 4L11 7L8 10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  kebab: <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="3" r="1.2" fill="currentColor" /><circle cx="7" cy="7" r="1.2" fill="currentColor" /><circle cx="7" cy="11" r="1.2" fill="currentColor" /></svg>,
  check: <svg width="10" height="10" viewBox="0 0 14 14" fill="none"><path d="M3 7.5L6 10L11 4" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  close: <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3 3L11 11M11 3L3 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>,
  mail: <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="1.5" y="3" width="11" height="8" rx="1.2" stroke="currentColor" strokeWidth="1.3" /><path d="M2 4l5 4 5-4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  invite: <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 2.5V8.5M4 5.5L7 2.5L10 5.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /><path d="M2.5 9.5V11.5H11.5V9.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" /></svg>,
  micro: <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="1.5" y="3.5" width="11" height="7" rx="1" stroke="currentColor" strokeWidth="1.3" /><circle cx="7" cy="7" r="1.4" fill="currentColor" /></svg>,
};

const SECTIONS = [
  { id: "account", title: "Account", icon: SIc.account },
  { id: "yard", title: "Yard", icon: SIc.yard },
  { id: "team", title: "Team", icon: SIc.team },
  { id: "prefs", title: "Preferences", icon: SIc.prefs },
  { id: "sensor", title: "Sensor", icon: SIc.sensor },
  { id: "danger", title: "Danger zone", icon: SIc.danger },
];

function SetTopBar({ yardName }: { yardName: string }) {
  const navigate = useNavigate();
  return (
    <div className="topbar set-topbar">
      <div className="topbar-inner">
        <button className="icon-btn" aria-label="Back to dashboard" onClick={() => navigate("/dashboard")}>
          {SIc.back}
        </button>
        <span className="set-tb-title">Settings</span>
        <span className="muted" style={{ fontSize: 12 }}>{yardName}</span>
      </div>
    </div>
  );
}

function SetRail({ active = "account" }: { active?: string }) {
  return (
    <nav className="set-rail" aria-label="Settings sections">
      {SECTIONS.map((s) => (
        <a key={s.id} href={`#${s.id}`} className={active === s.id ? "active" : ""}>
          <span className="set-rail-icon" aria-hidden="true">{s.icon}</span>
          {s.title}
        </a>
      ))}
      <div className="set-rail-divider"></div>
      <a href="#logout" style={{ color: "var(--ink-3)" }}>
        <span className="set-rail-icon" aria-hidden="true">{SIc.arrow}</span>
        Log out
      </a>
    </nav>
  );
}

interface SetSecProps {
  id: string;
  num: number;
  title: string;
  icon: React.ReactNode;
  desc?: string;
  danger?: boolean;
  children: React.ReactNode;
}

function SetSec({ id, num, title, icon, desc, danger, children }: SetSecProps) {
  return (
    <section id={id} className={`set-sec ${danger ? "danger" : ""}`}>
      <header className="set-sec-head">
        <span className="set-sec-num mono">{String(num).padStart(2, "0")}</span>
        <span className="set-sec-icon" aria-hidden="true">{icon}</span>
        <span className="set-sec-title">{title}</span>
      </header>
      {desc && <p className="set-sec-desc">{desc}</p>}
      <div className="set-sec-body">{children}</div>
    </section>
  );
}

interface SetRowProps {
  label: string;
  sub?: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
}

function SetRow({ label, sub, children, actions }: SetRowProps) {
  return (
    <div className="set-row">
      <div>
        <div className="set-row-label">{label}</div>
        {sub && <span className="set-row-label-sub">{sub}</span>}
      </div>
      <div className="set-row-value">{children}</div>
      {actions && <div className="set-row-actions">{actions}</div>}
    </div>
  );
}

function AccountSection({ name, email, emailVerified }: { name: string; email: string; emailVerified: boolean }) {
  return (
    <SetSec
      id="account"
      num={1}
      title="Account"
      icon={SIc.account}
      desc="Your personal login. Used to sign you into any yard you're a member of."
    >
      <SetRow label="Name">
        <input className="set-input" defaultValue={name} />
      </SetRow>
      <SetRow label="Email" sub="We send invite + password-reset emails here.">
        <span style={{ fontFamily: "var(--font-mono)", fontSize: 13 }}>{email}</span>
        {emailVerified && (
          <span className="set-verified">
            {SIc.check} Verified
          </span>
        )}
      </SetRow>
      <SetRow
        label="Password"
        sub="Last changed 14 March 2026."
        actions={<button className="btn ghost sm">Change password</button>}
      >
        <span className="mono" style={{ letterSpacing: "2px", color: "var(--ink-4)" }}>
          ••••••••••
        </span>
      </SetRow>
    </SetSec>
  );
}

function YardSection({ name, postcode, horseCount }: { name: string; postcode: string; horseCount: number }) {
  return (
    <SetSec
      id="yard"
      num={2}
      title="Yard"
      icon={SIc.yard}
      desc="The stables you manage. Postcode lets us auto-fill ambient temperature on every daily log."
    >
      <SetRow label="Yard name">
        <input className="set-input" defaultValue={name} />
      </SetRow>
      <SetRow label="UK postcode" sub="Required. Drives the weather lookup for hydration targets.">
        <input
          className="set-input mono"
          defaultValue={postcode}
          style={{ maxWidth: 160, textTransform: "uppercase" }}
        />
      </SetRow>
      <SetRow label="Horses">
        <div className="set-horse-count">
          <span className="set-horse-count-num">{horseCount}</span>
          <span className="set-horse-count-unit">on this yard</span>
          <Link to="/dashboard" className="set-horse-count-link link-btn">Manage horses →</Link>
        </div>
      </SetRow>
    </SetSec>
  );
}

interface TeamSectionProps {
  onInvite: () => void;
  onRemove: (m: MockMember) => void;
}

function TeamSection({ onInvite, onRemove }: TeamSectionProps) {
  const team = MOCK_TEAM;
  const isOwner = team.currentUserId === team.ownerUserId;
  return (
    <SetSec
      id="team"
      num={3}
      title="Team"
      icon={SIc.team}
      desc="Anyone you invite can log against any horse on the yard. Roles are flat for now. Read-only and vet roles coming later."
    >
      <div className="set-team-bar">
        <span className="set-team-bar-meta">
          <strong>{team.members.length} members</strong>
          {team.pending.length > 0 && (
            <>
              {" "}
              · {team.pending.length} pending invite
              {team.pending.length === 1 ? "" : "s"}
            </>
          )}
        </span>
        <button className="btn primary sm" onClick={onInvite}>
          {SIc.invite} Invite a teammate
        </button>
      </div>

      <div className="set-team-list">
        {team.members.map((m) => {
          const isYou = m.id === team.currentUserId;
          const canRemove = isOwner && !isYou && m.role !== "Owner";
          return (
            <div key={m.id} className="set-team-row">
              <span
                className="avatar"
                style={{
                  background: AVATAR_TONES[m.colour],
                  width: 36,
                  height: 36,
                  fontSize: 14,
                }}
              >
                {m.name[0]}
              </span>
              <div className="set-team-id">
                <span className="set-team-name">
                  {m.name}
                  {isYou && <span className="set-team-name-you">you</span>}
                </span>
                <span className="set-team-email">{m.email}</span>
              </div>
              <span className={`set-role-pill ${m.role.toLowerCase()}`}>{m.role}</span>
              <div className="set-team-meta">
                <span className="set-team-meta-active">{m.lastActive}</span>
                <span>joined {m.joined}</span>
              </div>
              <button
                className={`set-team-kebab ${canRemove ? "" : "is-hidden"}`}
                onClick={() => canRemove && onRemove(m)}
                aria-label={`Manage ${m.name}`}
              >
                {SIc.kebab}
              </button>
            </div>
          );
        })}
      </div>

      {team.pending.length > 0 && (
        <>
          <div className="set-pending-head">
            Pending invites
            <span className="set-pending-count">{team.pending.length}</span>
          </div>
          {team.pending.map((p) => (
            <div key={p.id} className="set-pending-row">
              <span className="set-pending-icon">{SIc.mail}</span>
              <span className="set-pending-email">{p.email}</span>
              <span className="set-pending-sent">{p.sent}</span>
              <div className="set-pending-actions">
                <button className="btn text">Resend</button>
                <button className="btn text danger">Revoke</button>
              </div>
            </div>
          ))}
        </>
      )}
    </SetSec>
  );
}

function PrefsSection() {
  const [prefs, setPrefs] = useState<MockPrefs>(MOCK_PREFS);
  const set = <K extends keyof MockPrefs>(k: K, v: MockPrefs[K]) =>
    setPrefs((p) => ({ ...p, [k]: v }));

  const seg = <K extends keyof MockPrefs>(k: K, opts: { k: MockPrefs[K]; label: string }[]) => (
    <div className="set-segmented">
      {opts.map((o) => (
        <button
          key={String(o.k)}
          className={`set-seg ${prefs[k] === o.k ? "active" : ""}`}
          onClick={() => set(k, o.k)}
          type="button"
        >
          {o.label}
        </button>
      ))}
    </div>
  );

  return (
    <SetSec
      id="prefs"
      num={4}
      title="Preferences"
      icon={SIc.prefs}
      desc="Display only. We always store data in metric. Switching units doesn't re-enter anything."
    >
      <SetRow label="Weight" sub="How horse + feed weights are shown.">
        {seg("weightUnit", [{ k: "kg", label: "kg" }, { k: "lb", label: "lb" }])}
      </SetRow>
      <SetRow label="Temperature" sub="Ambient temp on the daily log.">
        {seg("tempUnit", [{ k: "C", label: "°C" }, { k: "F", label: "°F" }])}
      </SetRow>
      <SetRow label="Water volume" sub="Daily intake + EHE targets.">
        {seg("volumeUnit", [{ k: "L", label: "litres" }, { k: "gal", label: "gallons" }])}
      </SetRow>
      <SetRow label="Time zone" sub="Used for 'logged 2h ago' timestamps and evening confirm nudges.">
        <div className="set-input-wrap" style={{ maxWidth: 280 }}>
          <select className="set-select" defaultValue={prefs.timezone}>
            <option value="Europe/London">Europe/London (GMT +1)</option>
            <option value="Europe/Dublin">Europe/Dublin (GMT +1)</option>
            <option value="Europe/Paris">Europe/Paris (GMT +2)</option>
          </select>
          <span className="set-select-chev">{SIc.chev}</span>
        </div>
      </SetRow>
    </SetSec>
  );
}

function SensorSection() {
  return (
    <SetSec id="sensor" num={5} title="Sensor" icon={SIc.sensor}>
      <div className="set-sensor-promo">
        <span className="set-sensor-icon">{SIc.micro}</span>
        <div className="set-sensor-text">
          <div className="set-sensor-title">W370 trough sensor · coming soon</div>
          <div className="set-sensor-sub">
            Automatic per-horse intake. No daily logging. Pairs with this yard. Early-access pricing for waitlist members.
          </div>
        </div>
        <button className="set-sensor-cta">Join waitlist →</button>
      </div>
    </SetSec>
  );
}

function DangerSection({ isOwner }: { isOwner: boolean }) {
  return (
    <SetSec id="danger" num={6} title="Danger zone" icon={SIc.danger} danger>
      <div className="set-danger-row">
        <div className="set-danger-text">
          <div className="set-danger-title">
            {isOwner ? "Delete this yard" : "Delete account"}
          </div>
          <div className="set-danger-sub">
            {isOwner
              ? "Permanently removes the yard, all horses, all daily logs, and revokes every member. Cannot be undone."
              : "Removes your account. Yards you own would need to be transferred or deleted first."}
          </div>
        </div>
        <button className="set-danger-btn">
          {isOwner ? "Delete yard…" : "Delete account…"}
        </button>
      </div>
    </SetSec>
  );
}

function InviteModal({ onClose }: { onClose: () => void }) {
  const [chips, setChips] = useState<string[]>(["beth.lane@gmail.com"]);
  const [draft, setDraft] = useState<string>("vet@grovevet.co.uk");
  return (
    <div className="set-modal-scrim" onClick={onClose}>
      <div className="set-modal" onClick={(e) => e.stopPropagation()}>
        <div className="set-modal-head">
          <div className="set-modal-head-text">
            <h3 className="set-modal-title">Invite teammates</h3>
            <p className="set-modal-sub">
              They'll get an email link. Once they sign up they can log against any horse on {MOCK_YARD.name}.
            </p>
          </div>
          <button className="set-modal-close" onClick={onClose} aria-label="Close">{SIc.close}</button>
        </div>
        <div className="set-modal-body">
          <div>
            <label className="set-modal-label">Email addresses</label>
            <div className="set-invite-chips" style={{ marginTop: 6 }}>
              {chips.map((c, i) => (
                <span key={i} className="set-invite-chip">
                  {c}
                  <button
                    onClick={() => setChips(chips.filter((_, idx) => idx !== i))}
                    aria-label={`Remove ${c}`}
                  >
                    {SIc.close}
                  </button>
                </span>
              ))}
              <input
                className="set-invite-input-inline"
                placeholder={chips.length === 0 ? "name@example.com" : "Add another…"}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
              />
            </div>
            <p className="set-modal-help" style={{ marginTop: 6 }}>
              Press Enter or comma to add more.
            </p>
          </div>
          <div>
            <label className="set-modal-label optional">Personal note</label>
            <textarea
              className="set-input"
              style={{ marginTop: 6, minHeight: 70, resize: "vertical" }}
              placeholder="Hey — invite from Sarah at Hollow Tree. We use this to log daily water and catch colic early."
            />
          </div>
        </div>
        <div className="set-modal-foot">
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn primary">
            Send {chips.length + (draft ? 1 : 0)} invite
            {(chips.length + (draft ? 1 : 0)) === 1 ? "" : "s"}
          </button>
        </div>
      </div>
    </div>
  );
}

function RemoveMemberModal({ member, onClose }: { member: MockMember; onClose: () => void }) {
  return (
    <div className="set-modal-scrim" onClick={onClose}>
      <div className="set-modal" onClick={(e) => e.stopPropagation()}>
        <div className="set-modal-head">
          <div className="set-modal-head-text">
            <h3 className="set-modal-title">Remove {member.name} from {MOCK_YARD.name}?</h3>
            <p className="set-modal-sub">
              They'll lose access immediately. Their past daily-log entries stay on file, attributed to them.
            </p>
          </div>
          <button className="set-modal-close" onClick={onClose} aria-label="Close">{SIc.close}</button>
        </div>
        <div className="set-modal-warning">
          <strong>{member.name}</strong> has logged against horses on 14 of the last 30 days. You can re-invite them later if needed.
        </div>
        <div className="set-modal-body">
          <label className="set-modal-label">Type their email to confirm</label>
          <input className="set-modal-confirm-name" placeholder={member.email} />
        </div>
        <div className="set-modal-foot">
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="set-danger-btn">Remove {member.name.split(" ")[0]}</button>
        </div>
      </div>
    </div>
  );
}

type ModalState =
  | { kind: "invite" }
  | { kind: "remove"; member: MockMember }
  | null;

export default function Settings() {
  const isMobile = useIsMobile();
  const { user, isDemo } = useCurrentUser();
  const { yard } = useUserYard(user?.uid);
  const [modal, setModal] = useState<ModalState>(null);
  const close = () => setModal(null);

  const accountName = isDemo
    ? MOCK_ACCOUNT.name
    : user?.displayName ?? user?.email?.split("@")[0] ?? "—";
  const accountEmail = isDemo ? MOCK_ACCOUNT.email : user?.email ?? "—";
  const emailVerified = isDemo ? MOCK_ACCOUNT.emailVerified : !!user?.emailVerified;

  const yardName = isDemo ? MOCK_YARD.name : yard?.name ?? "—";
  const yardPostcode = isDemo ? MOCK_YARD.postcode : yard?.postcode ?? "";
  const yardHorseCount = isDemo ? MOCK_HORSES.length : 0;
  const isOwner = isDemo
    ? MOCK_TEAM.currentUserId === MOCK_TEAM.ownerUserId
    : !!yard && yard.ownerUserId === user?.uid;

  return (
    <div className={`set ${isMobile ? "is-mobile" : "is-desktop"}`} style={{ position: "relative" }}>
      <SetTopBar yardName={yardName} />
      <main className="set-page">
        <div className="set-shell">
          <header className="set-head">
            <h1 className="set-h1">Settings</h1>
            <p className="set-h-sub">Manage your account, yard, team, and preferences.</p>
          </header>

          {!isMobile && <SetRail active="account" />}

          <div className="set-main">
            <AccountSection name={accountName} email={accountEmail} emailVerified={emailVerified} />
            <YardSection name={yardName} postcode={yardPostcode} horseCount={yardHorseCount} />
            {isDemo ? (
              <TeamSection
                onInvite={() => setModal({ kind: "invite" })}
                onRemove={(m) => setModal({ kind: "remove", member: m })}
              />
            ) : (
              <SetSec id="team" num={3} title="Team" icon={SIc.team}>
                <p className="set-sec-desc" style={{ margin: 0 }}>
                  Team invites are coming soon. For now it's just you on the yard.
                </p>
              </SetSec>
            )}
            <PrefsSection />
            <SensorSection />
            <DangerSection isOwner={isOwner} />
          </div>
        </div>
      </main>

      {modal?.kind === "invite" && <InviteModal onClose={close} />}
      {modal?.kind === "remove" && <RemoveMemberModal member={modal.member} onClose={close} />}
    </div>
  );
}
